<template>
  <SoDialog
    v-model="showTariffDialog"
    :card-title="getTitle"
    :loading="isLoading"
  >
    <SoErrorBoundary>
      <SoTariffComparison
        v-if="selectedAgreement && current.agreementsByMeterPoint?.isFetched"
        :agreements="[selectedAgreement]"
      />
    </SoErrorBoundary>
  </SoDialog>
  <div v-if="isLoading" class="d-flex gap-2">
    <SoSkeleton class="w-50" :type="['card']" />
    <SoSkeleton class="w-50" :type="['card']" />
  </div>
  <SoWidget v-else-if="hasMeterpoints" plain no-border>
    <SoRow>
      <SoCol
        v-for="(meterpoint, index) in current.meterpoints.data"
        :key="index"
        :default-width="6"
      >
        <SoWidget>
          <template #title>
            <div
              class="d-flex flex-wrap align-center"
              data-testid="tariff-meter-point-id"
              style="gap: 8px"
            >
              <MeterpointHeader :meterpoint="meterpoint" class="mb-2" />
              <v-spacer />
              <SoStatus :status="meterpoint.status" />
            </div>
          </template>
          <template #text>
            <MeterpointAgreement :meterpoint="meterpoint" />
          </template>
          <template
            v-if="meterpoint.status && getLatestTariff(meterpoint.identifier)"
            #actions
          >
            <SoButton
              title="See Details"
              type="link"
              class="ml-2"
              data-test-id="see-details-button"
              @click="handleMeterClick(meterpoint)"
            />
          </template>
        </SoWidget>
      </SoCol>
      <SoCol
        v-for="meterpoint in current.unavailableMeterpoints.data"
        :key="meterpoint.id"
        :default-width="6"
      >
        <SoWidget>
          <template #title>
            <MeterpointHeader :meterpoint="meterpoint" class="mb-2" />
          </template>
          <template #text>
            <UnavailableMeterpointAlert :meterpoint="meterpoint" />
          </template>
        </SoWidget>
      </SoCol>
    </SoRow>
  </SoWidget>
  <SoAlert
    v-else
    class="my-2"
    type="info"
    title="No meterpoint details available"
    data-testid="no-meter-points-available"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoWidget from "@/components/SoWidget.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoTariffComparison from "@/components/SoTariffComparison.vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import UnavailableMeterpointAlert from "@/views/customer/components/UnavailableMeterpointAlert.vue"
  import MeterpointAgreement from "@/views/customer/overview/components/MeterpointAgreement.vue"
  import { IMeterPointAgreement } from "@/models/meterpointAgreement/meterpointAgreement.types"

  export default defineComponent({
    components: {
      MeterpointAgreement,
      MeterpointHeader,
      UnavailableMeterpointAlert,
      SoWidget,
      SoSkeleton,
      SoButton,
      SoStatus,
      SoRow,
      SoCol,
      SoAlert,
      SoDialog,
      SoErrorBoundary,
      SoTariffComparison,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        showTariffDialog: false,
        selectedAgreement: null as IMeterPointAgreement | null,
      }
    },
    computed: {
      getTitle(): string {
        return this.selectedAgreement?.productDetails.displayName || ""
      },
      hasMeterpoints(): boolean {
        return (
          this.current.meterpoints.data.length > 0 ||
          this.current.unavailableMeterpoints.data.length > 0
        )
      },
      isLoading(): boolean {
        return (
          this.current.meterpoints.isFetching ||
          this.current.agreementsByMeterPoint.isFetching
        )
      },
    },
    methods: {
      getLatestTariff(identifier: string): IMeterPointAgreement | null {
        if (!this.current.agreementsByMeterPoint.data[identifier]) return null
        const agreementsCopy = [
          ...this.current.agreementsByMeterPoint.data[identifier],
        ]
        const meterpointAgreements = agreementsCopy.sort((a, b) => {
          if (a.toDate == null) return -1
          if (b.toDate == null) return 1

          return a.toDate.isAfter(b.toDate) ? -1 : 1
        })
        return meterpointAgreements[0]
      },
      handleMeterClick(meterpoint: IMeterpoint) {
        this.selectedAgreement = this.getLatestTariff(meterpoint.identifier)
        this.showTariffDialog = true
      },
    },
  })
</script>
